import React, { useRef, useEffect, useState } from 'react';
import { 
  ImageAdjustments, 
  TextElement, 
  ShapeElement, 
  AssetElement 
} from '../../../types/thumbnailTypes';
import { Resolution } from './ResolutionSelector';
import styles from './ThumbnailCanvas.module.css';

interface ThumbnailCanvasProps {
  baseImage: string;
  imageAdjustments: ImageAdjustments;
  textElements: TextElement[];
  shapeElements: ShapeElement[];
  assetElements: AssetElement[];
  selectedElement: string | null;
  onSelectElement: (elementId: string | null) => void;
  onUpdateTextElement: (id: string, updates: Partial<TextElement>) => void;
  onUpdateShapeElement: (id: string, updates: Partial<ShapeElement>) => void;
  onUpdateAssetElement: (id: string, updates: Partial<AssetElement>) => void;
  onUpdateImageAdjustments?: (adjustments: Partial<ImageAdjustments>) => void;
  onCanvasDimensionsChange?: (dimensions: { width: number; height: number }) => void;
  canvasSettings: {
    backgroundColor?: string;
    backgroundOpacity?: number;
  };
  targetResolution?: Resolution;
}

const ThumbnailCanvas: React.FC<ThumbnailCanvasProps> = ({
  baseImage,
  imageAdjustments,
  textElements,
  shapeElements,
  assetElements,
  selectedElement,
  onSelectElement,
  onUpdateTextElement,
  onUpdateShapeElement,
  onUpdateAssetElement,
  onUpdateImageAdjustments,
  onCanvasDimensionsChange,
  canvasSettings,
  targetResolution
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragElement, setDragElement] = useState<string | null>(null);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [isResizingImage, setIsResizingImage] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [initialScale, setInitialScale] = useState({ scaleX: 1, scaleY: 1 });
  const [justClickedElement, setJustClickedElement] = useState(false);
  const [isResizingShape, setIsResizingShape] = useState(false);
  const [isResizingAsset, setIsResizingAsset] = useState(false);
  const [initialDimensions, setInitialDimensions] = useState({ width: 0, height: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });

  // Track canvas dimensions and report changes
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const updateCanvasDimensions = () => {
      // Debounce rapid dimension changes
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (canvasRef.current && onCanvasDimensionsChange) {
          const rect = canvasRef.current.getBoundingClientRect();
          const newDimensions = {
            width: Math.round(rect.width),
            height: Math.round(rect.height)
          };
          onCanvasDimensionsChange(newDimensions);
        }
      }, 100); // 100ms debounce
    };

    // Initial measurement - delay to ensure canvas is rendered
    const initialTimeout = setTimeout(updateCanvasDimensions, 50);

    // Listen for window resize
    window.addEventListener('resize', updateCanvasDimensions);
    
    // Use ResizeObserver for more precise detection
    let resizeObserver: ResizeObserver | null = null;
    if (canvasRef.current && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(updateCanvasDimensions);
      resizeObserver.observe(canvasRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(initialTimeout);
      window.removeEventListener('resize', updateCanvasDimensions);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [onCanvasDimensionsChange]);

  // Add global mouse event listeners for dragging and resizing
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isResizingShape && resizeHandle && dragElement) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        
        const shapeElement = shapeElements.find(el => el.id === dragElement);
        if (!shapeElement) return;
        
        let newWidth = initialDimensions.width;
        let newHeight = initialDimensions.height;
        let newX = initialPosition.x;
        let newY = initialPosition.y;
        
        // Handle different resize handles for shapes
        switch (resizeHandle) {
          case 'nw': // Northwest corner
            newWidth = Math.max(20, initialDimensions.width - deltaX);
            newHeight = Math.max(20, initialDimensions.height - deltaY);
            newX = initialPosition.x + (initialDimensions.width - newWidth);
            newY = initialPosition.y + (initialDimensions.height - newHeight);
            break;
          case 'ne': // Northeast corner
            newWidth = Math.max(20, initialDimensions.width + deltaX);
            newHeight = Math.max(20, initialDimensions.height - deltaY);
            newY = initialPosition.y + (initialDimensions.height - newHeight);
            break;
          case 'sw': // Southwest corner
            newWidth = Math.max(20, initialDimensions.width - deltaX);
            newHeight = Math.max(20, initialDimensions.height + deltaY);
            newX = initialPosition.x + (initialDimensions.width - newWidth);
            break;
          case 'se': // Southeast corner
            newWidth = Math.max(20, initialDimensions.width + deltaX);
            newHeight = Math.max(20, initialDimensions.height + deltaY);
            break;
          case 'n': // North edge
            newHeight = Math.max(20, initialDimensions.height - deltaY);
            newY = initialPosition.y + (initialDimensions.height - newHeight);
            break;
          case 's': // South edge
            newHeight = Math.max(20, initialDimensions.height + deltaY);
            break;
          case 'w': // West edge
            newWidth = Math.max(20, initialDimensions.width - deltaX);
            newX = initialPosition.x + (initialDimensions.width - newWidth);
            break;
          case 'e': // East edge
            newWidth = Math.max(20, initialDimensions.width + deltaX);
            break;
        }
        
        onUpdateShapeElement(dragElement, {
          dimensions: { width: newWidth, height: newHeight },
          position: { x: newX, y: newY }
        });
        
        return;
      }

      if (isResizingAsset && resizeHandle && dragElement) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        
        const assetElement = assetElements.find(el => el.id === dragElement);
        if (!assetElement) return;
        
        let newWidth = initialDimensions.width;
        let newHeight = initialDimensions.height;
        let newX = initialPosition.x;
        let newY = initialPosition.y;
        
        // Handle different resize handles for assets
        switch (resizeHandle) {
          case 'nw': // Northwest corner - proportional resize
          case 'ne': // Northeast corner - proportional resize  
          case 'sw': // Southwest corner - proportional resize
          case 'se': // Southeast corner - proportional resize
            {
              // Calculate proportional resize based on the dominant movement
              const deltaX = e.clientX - dragStart.x;
              const deltaY = e.clientY - dragStart.y;
              
              // Use the average of both deltas for proportional scaling
              let delta = 0;
              switch (resizeHandle) {
                case 'nw':
                  delta = -(deltaX + deltaY) * 0.5; // Drag away from corner to grow
                  break;
                case 'ne':
                  delta = (deltaX - deltaY) * 0.5; // Drag away from corner to grow
                  break;
                case 'sw':
                  delta = (-deltaX + deltaY) * 0.5; // Drag away from corner to grow
                  break;
                case 'se':
                  delta = (deltaX + deltaY) * 0.5; // Drag away from corner to grow
                  break;
              }
              
              // Calculate aspect ratio from initial dimensions
              const aspectRatio = initialDimensions.width / initialDimensions.height;
              // Apply proportional scaling
              newWidth = Math.max(20, initialDimensions.width + delta);
              newHeight = Math.max(20, newWidth / aspectRatio);
              
              // Ensure minimum height is maintained
              if (newHeight < 20) {
                newHeight = 20;
                newWidth = newHeight * aspectRatio;
              }
              
              // Update position based on handle
              switch (resizeHandle) {
                case 'nw':
                  newX = initialPosition.x + (initialDimensions.width - newWidth);
                  newY = initialPosition.y + (initialDimensions.height - newHeight);
                  break;
                case 'ne':
                  newY = initialPosition.y + (initialDimensions.height - newHeight);
                  break;
                case 'sw':
                  newX = initialPosition.x + (initialDimensions.width - newWidth);
                  break;
                case 'se':
                  // No position adjustment needed
                  break;
              }
            }
            break;
          case 'n': // North edge - height only
            newHeight = Math.max(20, initialDimensions.height - deltaY);
            newWidth = initialDimensions.width;
            newY = initialPosition.y + (initialDimensions.height - newHeight);
            break;
          case 's': // South edge - height only
            newHeight = Math.max(20, initialDimensions.height + deltaY);
            newWidth = initialDimensions.width;
            break;
          case 'w': // West edge - width only
            newWidth = Math.max(20, initialDimensions.width - deltaX);
            newHeight = initialDimensions.height;
            newX = initialPosition.x + (initialDimensions.width - newWidth);
            break;
          case 'e': // East edge - width only
            newWidth = Math.max(20, initialDimensions.width + deltaX);
            newHeight = initialDimensions.height;
            break;
        }
        
        onUpdateAssetElement(dragElement, {
          dimensions: { width: newWidth, height: newHeight },
          position: { x: newX, y: newY }
        });
        
        return;
      }

      if (isResizingImage && resizeHandle) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        
        // Use a simple, consistent scale factor based on movement
        const sensitivity = 0.002; // Adjust sensitivity as needed
        let scaleChange = 0;
        
        // Calculate scale change based on handle direction
        switch (resizeHandle) {
          case 'nw': // Northwest corner - drag away from corner to grow
            scaleChange = -(deltaX + deltaY) * sensitivity * 0.5;
            break;
          case 'ne': // Northeast corner - drag away from corner to grow
            scaleChange = (deltaX - deltaY) * sensitivity * 0.5;
            break;
          case 'sw': // Southwest corner - drag away from corner to grow
            scaleChange = (-deltaX + deltaY) * sensitivity * 0.5;
            break;
          case 'se': // Southeast corner - drag away from corner to grow
            scaleChange = (deltaX + deltaY) * sensitivity * 0.5;
            break;
        }
        
        // Apply scale change maintaining aspect ratio and bounds
        const newScaleX = Math.max(0.1, Math.min(3, initialScale.scaleX + scaleChange));
        const newScaleY = Math.max(0.1, Math.min(3, initialScale.scaleY + scaleChange));
        
        onUpdateImageAdjustments?.({
          scale: { scaleX: newScaleX, scaleY: newScaleY }
        });
        
        return;
      }

      if (isDraggingImage) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;

        // Update image position through the parent component
        const currentX = imageAdjustments.position?.x || 0;
        const currentY = imageAdjustments.position?.y || 0;
        
        // Call the onUpdateImageAdjustments prop
        onUpdateImageAdjustments?.({
          position: {
            x: currentX + deltaX,
            y: currentY + deltaY
          }
        });

        setDragStart({ x: e.clientX, y: e.clientY });
        return;
      }

      if (!isDragging || !dragElement) return;

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      // Update element position
      const textElement = textElements.find(el => el.id === dragElement);
      if (textElement) {
        const newPosition = {
          x: textElement.position.x + deltaX,
          y: textElement.position.y + deltaY
        };
        console.log('Updating text position:', { 
          id: dragElement, 
          old: textElement.position, 
          delta: { deltaX, deltaY },
          new: newPosition 
        });
        onUpdateTextElement(dragElement, {
          position: newPosition
        });
      }

      const shapeElement = shapeElements.find(el => el.id === dragElement);
      if (shapeElement) {
        onUpdateShapeElement(dragElement, {
          position: {
            x: shapeElement.position.x + deltaX,
            y: shapeElement.position.y + deltaY
          }
        });
      }

      // Handle asset element dragging
      const assetElement = assetElements.find(el => el.id === dragElement);
      if (assetElement) {
        onUpdateAssetElement(dragElement, {
          position: {
            x: assetElement.position.x + deltaX,
            y: assetElement.position.y + deltaY
          }
        });
      }

      setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
      setDragElement(null);
      setIsDraggingImage(false);
      setIsResizingImage(false);
      setIsResizingShape(false);
      setIsResizingAsset(false);
      setResizeHandle(null);
    };

    if (isDragging || isDraggingImage || isResizingImage || isResizingShape || isResizingAsset) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.body.style.cursor = (isResizingImage || isResizingShape || isResizingAsset) ? 'nwse-resize' : 'grabbing';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, isDraggingImage, isResizingImage, isResizingShape, isResizingAsset, resizeHandle, dragElement, dragStart, initialScale, initialDimensions, textElements, shapeElements, assetElements, imageAdjustments.position, onUpdateTextElement, onUpdateShapeElement, onUpdateAssetElement, onUpdateImageAdjustments]);

  const handleCanvasClick = (e: React.MouseEvent) => {
    // If we just clicked on an element, don't process canvas click
    if (justClickedElement) {
      setJustClickedElement(false);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if click is on any element, otherwise deselect
    let clickedElement = null;
    
    // Check text elements (simplified - in real implementation would check bounds)
    for (const element of textElements) {
      if (
        x >= element.position.x - 50 && 
        x <= element.position.x + 200 &&
        y >= element.position.y - 25 && 
        y <= element.position.y + 50
      ) {
        clickedElement = element.id;
        break;
      }
    }

    // Check shape elements
    if (!clickedElement) {
      for (const element of shapeElements) {
        if (
          x >= element.position.x && 
          x <= element.position.x + element.dimensions.width &&
          y >= element.position.y && 
          y <= element.position.y + element.dimensions.height
        ) {
          clickedElement = element.id;
          break;
        }
      }
    }

    // Only deselect if we clicked on empty canvas (not on the image)
    onSelectElement(clickedElement);
  };

  const handleImageMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Set flag to prevent canvas click from deselecting
    setJustClickedElement(true);
    
    // Select the image first
    onSelectElement('base-image');
    
    // Set up dragging
    setIsDraggingImage(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleShapeResizeMouseDown = (e: React.MouseEvent, handle: string, shapeId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Shape resize mousedown:', handle, 'for shape:', shapeId);
    
    // Set flag to prevent canvas click from deselecting
    setJustClickedElement(true);
    
    const shapeElement = shapeElements.find(el => el.id === shapeId);
    if (!shapeElement) return;
    
    setIsResizingShape(true);
    setResizeHandle(handle);
    setDragElement(shapeId);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialDimensions({
      width: shapeElement.dimensions.width,
      height: shapeElement.dimensions.height
    });
    setInitialPosition({
      x: shapeElement.position.x,
      y: shapeElement.position.y
    });
  };

  const handleAssetResizeMouseDown = (e: React.MouseEvent, handle: string, assetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Set flag to prevent canvas click from deselecting
    setJustClickedElement(true);
    
    const assetElement = assetElements.find(el => el.id === assetId);
    if (!assetElement) return;
    
    setIsResizingAsset(true);
    setResizeHandle(handle);
    setDragElement(assetId);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialDimensions({
      width: assetElement.dimensions.width,
      height: assetElement.dimensions.height
    });
    setInitialPosition({
      x: assetElement.position.x,
      y: assetElement.position.y
    });
  };

  const handleResizeMouseDown = (e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Set flag to prevent canvas click from deselecting
    setJustClickedElement(true);
    
    setIsResizingImage(true);
    setResizeHandle(handle);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialScale({
      scaleX: imageAdjustments.scale?.scaleX || 1,
      scaleY: imageAdjustments.scale?.scaleY || 1
    });
  };

  const handleMouseDown = (e: React.MouseEvent, elementId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Set flag to prevent canvas click from deselecting
    setJustClickedElement(true);
    
    setIsDragging(true);
    setDragElement(elementId);
    setDragStart({ x: e.clientX, y: e.clientY });
    onSelectElement(elementId);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // Local mouse move is only used for hover effects if needed
    // Actual dragging is handled by global event listeners
  };

  const handleMouseUp = () => {
    // Mouse up is handled by global event listeners
  };
  // Apply image adjustments as CSS filters
  const getImageStyle = () => {
    const filters = [];
    
    if (imageAdjustments.contrast !== 0) {
      filters.push(`contrast(${100 + imageAdjustments.contrast}%)`);
    }
    if (imageAdjustments.brightness !== 0) {
      filters.push(`brightness(${100 + imageAdjustments.brightness}%)`);
    }
    if (imageAdjustments.blackAndWhite) {
      filters.push('grayscale(100%)');
    }

    return {
      filter: filters.length > 0 ? filters.join(' ') : 'none',
      transform: `
        translate(${imageAdjustments.position?.x || 0}px, ${imageAdjustments.position?.y || 0}px)
        scale(${imageAdjustments.scale?.scaleX || 1}, ${imageAdjustments.scale?.scaleY || 1})
        rotate(${imageAdjustments.rotation || 0}deg)
      `
    };
  };

  // Calculate aspect ratio for canvas display
  const aspectRatio = targetResolution ? targetResolution.width / targetResolution.height : 16/9;
  
  // Determine canvas size based on aspect ratio
  const isSquare = Math.abs(aspectRatio - 1) < 0.1; // 1:1 ratio
  const isPortrait = aspectRatio < 0.7; // 9:16 and similar tall ratios
  
  let canvasClass = styles.canvas;
  if (isSquare) {
    canvasClass += ` ${styles.canvasLarge}`; // Double size for 1:1
  } else if (isPortrait) {
    canvasClass += ` ${styles.canvasSmall}`; // Smaller size for 9:16
  }
  
  return (
    <div className={styles.canvasContainer}>
      <div 
        ref={canvasRef}
        className={canvasClass}
        onClick={handleCanvasClick}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{
          backgroundColor: canvasSettings.backgroundColor || '#000000',
          opacity: (canvasSettings.backgroundOpacity || 100) / 100,
          aspectRatio: aspectRatio.toString() // Dynamic aspect ratio based on target resolution
        }}
      >
        {/* Base Image */}
        {baseImage && (
          <>
            <img
              src={baseImage}
              alt="Thumbnail base"
              className={`${styles.baseImage} ${isDraggingImage ? styles.dragging : ''} ${selectedElement === 'base-image' ? styles.selected : ''}`}
              style={getImageStyle()}
              onMouseDown={handleImageMouseDown}
            />
            
            {/* Resize handles - only show when image is selected */}
            {selectedElement === 'base-image' && (
              <div 
                className={styles.resizeHandles}
                style={getImageStyle()}
              >
                {/* All Corner handles */}
                <div 
                  className={`${styles.resizeHandle} ${styles.nw}`}
                  onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
                />
                <div 
                  className={`${styles.resizeHandle} ${styles.ne}`}
                  onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
                />
                <div 
                  className={`${styles.resizeHandle} ${styles.sw}`}
                  onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
                />
                <div 
                  className={`${styles.resizeHandle} ${styles.se}`}
                  onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
                />
              </div>
            )}
          </>
        )}

        {/* No base image placeholder */}
        {!baseImage && (
          <div className={styles.placeholder}>
            <div className={styles.placeholderIcon}>🖼️</div>
            <p>No base image</p>
            <p>Generate or upload a thumbnail first</p>
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
              Generated thumbnails preserve original for editing
            </p>
          </div>
        )}

        {/* Shape Elements */}
        {shapeElements.map(element => (
          <div key={element.id} style={{ position: 'relative' }}>
            <div
              className={`${styles.shapeElement} ${selectedElement === element.id ? styles.selected : ''}`}
              style={{
                left: element.position.x,
                top: element.position.y,
                width: element.dimensions.width,
                height: element.dimensions.height,
                backgroundColor: element.style.fillColor,
                opacity: element.style.fillOpacity / 100,
                borderRadius: element.type === 'circle' ? '50%' : '0',
                border: element.style.borderWidth ? 
                  `${element.style.borderWidth}px solid ${element.style.borderColor}` : 
                  'none',
                transform: `rotate(${element.rotation || 0}deg)`,
                cursor: isDragging && dragElement === element.id ? 'grabbing' : 'grab'
              }}
              onMouseDown={(e) => handleMouseDown(e, element.id)}
            />
            
            {/* Shape Resize handles - only show when shape is selected */}
            {selectedElement === element.id && (
              <div 
                className={styles.shapeResizeHandles}
                style={{
                  left: element.position.x,
                  top: element.position.y,
                  width: element.dimensions.width,
                  height: element.dimensions.height,
                }}
              >
                {/* Corner handles */}
                <div 
                  className={`${styles.shapeResizeHandle} ${styles.nw}`}
                  onMouseDown={(e) => handleShapeResizeMouseDown(e, 'nw', element.id)}
                />
                <div 
                  className={`${styles.shapeResizeHandle} ${styles.ne}`}
                  onMouseDown={(e) => handleShapeResizeMouseDown(e, 'ne', element.id)}
                />
                <div 
                  className={`${styles.shapeResizeHandle} ${styles.sw}`}
                  onMouseDown={(e) => handleShapeResizeMouseDown(e, 'sw', element.id)}
                />
                <div 
                  className={`${styles.shapeResizeHandle} ${styles.se}`}
                  onMouseDown={(e) => handleShapeResizeMouseDown(e, 'se', element.id)}
                />
                
                {/* Edge handles */}
                <div 
                  className={`${styles.shapeResizeHandle} ${styles.n}`}
                  onMouseDown={(e) => handleShapeResizeMouseDown(e, 'n', element.id)}
                />
                <div 
                  className={`${styles.shapeResizeHandle} ${styles.s}`}
                  onMouseDown={(e) => handleShapeResizeMouseDown(e, 's', element.id)}
                />
                <div 
                  className={`${styles.shapeResizeHandle} ${styles.w}`}
                  onMouseDown={(e) => handleShapeResizeMouseDown(e, 'w', element.id)}
                />
                <div 
                  className={`${styles.shapeResizeHandle} ${styles.e}`}
                  onMouseDown={(e) => handleShapeResizeMouseDown(e, 'e', element.id)}
                />
              </div>
            )}
          </div>
        ))}

        {/* Text Elements */}
        {textElements.map(element => {
          // For text alignment, we need to ensure the container has a minimum width
          // Calculate a reasonable width based on font size and text length
          // Using a tighter calculation to keep the outline smaller
          const estimatedWidth = Math.max(200, element.text.length * (element.style.fontSize * 0.55));
          
          // Adjust the left position based on text alignment
          // The position.x from dragging is preserved, but we adjust the container based on alignment
          const textAlign = element.style.textAlign || 'left';
          let adjustedLeft: number;
          
          if (textAlign === 'left') {
            // Left align: position.x is the left edge of the container
            adjustedLeft = element.position.x;
          } else if (textAlign === 'center') {
            // Center align: position.x is the center, so shift left by half width
            adjustedLeft = element.position.x - (estimatedWidth / 2);
          } else if (textAlign === 'right') {
            // Right align: position.x is the right edge, so shift left by full width
            adjustedLeft = element.position.x - estimatedWidth;
          } else {
            adjustedLeft = element.position.x;
          }
          
          return (
            <div
              key={element.id}
              className={`${styles.textElement} ${selectedElement === element.id ? styles.selected : ''}`}
              style={{
                left: adjustedLeft,
                top: element.position.y,
                width: `${estimatedWidth}px`,
                fontSize: element.style.fontSize,
                fontFamily: element.style.fontFamily,
                color: element.style.color,
                textAlign: textAlign,
                textShadow: element.style.shadow?.enabled ? 
                  `${element.style.shadow.offsetX}px ${element.style.shadow.offsetY}px ${element.style.shadow.blur}px ${element.style.shadow.color}` : 
                  'none',
                WebkitTextStroke: element.style.border?.enabled ? 
                  `${element.style.border.size}px ${element.style.border.color}` : 
                  '0px transparent',
                backgroundColor: element.style.background?.enabled ? 
                  `${element.style.background.color}${Math.round((element.style.background.opacity / 100) * 255).toString(16).padStart(2, '0')}` : 
                  'transparent',
                padding: element.style.background?.enabled ? 
                  element.style.background.padding : 
                  0,
                cursor: isDragging && dragElement === element.id ? 'grabbing' : 'grab'
              }}
              onMouseDown={(e) => handleMouseDown(e, element.id)}
            >
              {element.text}
            </div>
          );
        })}

        {/* Asset Elements */}
        {assetElements.map(element => (
          <div key={element.id}>
            <img
              src={element.assetUrl}
              alt="Asset"
              className={`${styles.assetElement} ${selectedElement === element.id ? styles.selected : ''}`}
              style={{
                left: element.position.x,
                top: element.position.y,
                width: element.dimensions.width,
                height: element.dimensions.height,
                opacity: element.opacity / 100,
                transform: `rotate(${element.rotation || 0}deg)`,
                cursor: isDragging && dragElement === element.id ? 'grabbing' : 'grab'
              }}
              onMouseDown={(e) => handleMouseDown(e, element.id)}
            />
            
            {/* Asset Resize handles - only show when asset is selected */}
            {selectedElement === element.id && (
              <div 
                className={styles.assetResizeHandles}
                style={{
                  left: element.position.x,
                  top: element.position.y,
                  width: element.dimensions.width,
                  height: element.dimensions.height,
                }}
              >
                {/* Corner handles */}
                <div 
                  className={`${styles.assetResizeHandle} ${styles.nw}`}
                  onMouseDown={(e) => handleAssetResizeMouseDown(e, 'nw', element.id)}
                />
                <div 
                  className={`${styles.assetResizeHandle} ${styles.ne}`}
                  onMouseDown={(e) => handleAssetResizeMouseDown(e, 'ne', element.id)}
                />
                <div 
                  className={`${styles.assetResizeHandle} ${styles.sw}`}
                  onMouseDown={(e) => handleAssetResizeMouseDown(e, 'sw', element.id)}
                />
                <div 
                  className={`${styles.assetResizeHandle} ${styles.se}`}
                  onMouseDown={(e) => handleAssetResizeMouseDown(e, 'se', element.id)}
                />
                
                {/* Edge handles */}
                <div 
                  className={`${styles.assetResizeHandle} ${styles.n}`}
                  onMouseDown={(e) => handleAssetResizeMouseDown(e, 'n', element.id)}
                />
                <div 
                  className={`${styles.assetResizeHandle} ${styles.s}`}
                  onMouseDown={(e) => handleAssetResizeMouseDown(e, 's', element.id)}
                />
                <div 
                  className={`${styles.assetResizeHandle} ${styles.w}`}
                  onMouseDown={(e) => handleAssetResizeMouseDown(e, 'w', element.id)}
                />
                <div 
                  className={`${styles.assetResizeHandle} ${styles.e}`}
                  onMouseDown={(e) => handleAssetResizeMouseDown(e, 'e', element.id)}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThumbnailCanvas;